import { useState } from 'react'

import { cn } from '@/lib/utils'
import { AgentSurface } from './AgentSurface'
import { Message, Person } from './contacts'
import { ContactStrip } from './ContactStrip'

/** Wide enough to read a thread in, narrow enough to leave the page the room. */
export const CHAT_PANE_WIDTH = 300

/**
 * The agent pinned open down the right edge, rather than summoned over the
 * page. Nothing opens or closes it: that is the point of this placement. It
 * sits right because it now holds the people too, and that is the edge help
 * and comment threads live on.
 */
export function ChatPane({
  notified,
  className,
  width = CHAT_PANE_WIDTH,
  contentWidth,
  connect,
  hideAgentLabel,
  blankChat,
  hideTabBar,
  banner,
  people,
  agentTint,
  agentThread,
  composerLabel,
  bodyFaded,
  hideContacts,
  disabled,
  composerFooter,
  softComposer,
  showChat,
}: {
  notified?: boolean
  className?: string
  /** A fixed column, or 'fill' when the pane is the page rather than beside one. */
  width?: number | 'fill'
  /** Holds the thread to this width while the pane — and its tab bar — run wider. */
  contentWidth?: number
  /** Adds the Connect tab — the fleet assistant's fourth surface. */
  connect?: boolean
  /** Drops the agent's name chip when it would only repeat the brand. */
  hideAgentLabel?: boolean
  /** Opens on nothing said yet. */
  blankChat?: boolean
  /** Drops the tab bar, for an agent with only the one surface. */
  hideTabBar?: boolean
  /** A note above the thread. */
  banner?: React.ReactNode
  /** Who can be pulled in. Defaults to everyone the account knows. */
  people?: Person[]
  /** The agent's face colour, where it is not Cai's own. */
  agentTint?: string
  /** The agent's own thread, where this pane opens on something else. */
  agentThread?: Message[]
  /** Replaces "Message <name>", where this pane is not the account's own chat. */
  composerLabel?: string
  /** Hides the thread and composer while the pane resizes around them. */
  bodyFaded?: boolean
  /** Drops the strip, where the agent's face has moved somewhere else. */
  hideContacts?: boolean
  /** The agent cannot help on this surface yet. */
  disabled?: boolean
  /** Sits under the composer — trial state, and the like. */
  composerFooter?: React.ReactNode
  /** The setup flow's composer: a recessed surface, rounder, green to send. */
  softComposer?: boolean
  /** Bumped to send the pane back to the conversation. */
  showChat?: number
}) {
  const [person, setPerson] = useState<Person | null>(null)
  // Pulling someone into the room is something you do to the conversation, so
  // it opens the conversation. Summed with the caller's signal, so either can
  // ask for the chat without cancelling the other out.
  const [picked, setPicked] = useState(0)

  return (
    <div
      style={width === 'fill' ? undefined : { width }}
      className={cn(
        'flex h-full flex-col bg-white',
        width === 'fill' ? 'min-w-0 flex-1' : 'shrink-0',
        className
      )}
    >
      {!hideContacts && (
        <ContactStrip
          person={person}
          onSelect={(next) => {
            setPerson(next)
            setPicked((count) => count + 1)
          }}
          unread={notified}
          hideAgentLabel={hideAgentLabel}
          people={people}
          agentTint={agentTint}
        />
      )}
      <AgentSurface
        person={person}
        notified={notified}
        contentWidth={contentWidth}
        connect={connect}
        blankChat={blankChat}
        hideTabBar={hideTabBar}
        banner={banner}
        agentThread={agentThread}
        composerLabel={composerLabel}
        bodyFaded={bodyFaded}
        disabled={disabled}
        composerFooter={composerFooter}
        softComposer={softComposer}
        showChat={(showChat ?? 0) + picked}
      />
    </div>
  )
}
